import type { StagedContext } from '@/vantage/types/Context.types';
import { t } from 'i18next';
import { MAX_POSTGRES_EPOCH_MILLISEC, VantageNames } from '@/vantage/constants';
import { setActiveExtensions } from '@/vantage/vantageWorkbook.actions';
import { sqVantageContextStore, sqVantageWorkbookStore } from '@/core/core.stores';
import { safeGetJsonProperty } from '@/utilities/httpHelpers.utilities';
import { isUserDefinedContextType } from './components/extensionConfig/extensionConfig.types';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { capitalizeString } from '@/utilities/stringHelper.utilities';
import type { UserDefinedContextType } from './components/extensionConfig/extensionConfig.types';
import type { ExtensionSchema } from '@/vantage/types/Extension.types';

export type SeeqUser = {
  email: string;
  firstName: string;
  id: string;
  isActive: boolean;
  isAdmin: boolean;
  isArchived: boolean;
  isEnabled: boolean;
  isPasswordSettable: boolean;
  isRedacted: boolean;
  lastLoginAt: string;
  lastName: string;
  name: string;
  type: string;
  username: string;
  groups: any[];
};

export function snakeToCamel(obj: any): any {
  if (typeof obj !== 'object' || obj === null) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(snakeToCamel);
  }

  return Object.entries(obj).reduce((acc, [key, value]) => {
    const camelKey = key.replace(/_\w/g, function (match) {
      return match[1].toUpperCase();
    });

    return {
      ...acc,
      [camelKey]: snakeToCamel(value),
    };
  }, {});
}

export function mergeData<T>(newData: T[], existingData: T[], getKey: (row: T) => string): T[] {
  const existingMap = new Map<string, any>(existingData?.map((r) => [getKey(r), r]));
  newData.forEach((r) => existingMap.set(getKey(r), r));
  return Array.from(existingMap.values()).filter((capsule) => !capsule[VantageNames.Columns.DeletedAt]);
}

export const serializeNote = (stagedContext: StagedContext[]) => {
  const userContext =
    stagedContext.find((context) => context.identifier === VantageNames.Extensions.Notes)?.content?.value || '';
  const otherExtensions = stagedContext.filter((context) => context.identifier !== VantageNames.Extensions.Notes);

  const serializedNote = {
    identifier: VantageNames.Extensions.Notes,
    content: { value: userContext },
    extensionContexts: [...otherExtensions],
  };
  return [...otherExtensions, serializedNote];
};

export function parseAsCamel(s: string) {
  return s ? snakeToCamel(JSON.parse(s)) : undefined;
}

type CoercableToDate = number | string | Date;

export function ensureDate(x: CoercableToDate | null): Date | undefined {
  if (x) {
    return typeof x === 'number' ? new Date(x / 1e6) : new Date(x);
  }
}

export function ensureIso(x: CoercableToDate | null): string | undefined {
  return ensureDate(x)?.toISOString();
}

export function isMaxEnd(x: CoercableToDate | null): boolean {
  return ensureDate(x)?.valueOf() === MAX_POSTGRES_EPOCH_MILLISEC;
}

export const getUniqueColor = (usedColors: Set<string>) => {
  let color;
  do {
    color = `#${Math.floor(Math.random() * 0xffffff)
      .toString(16)
      .padStart(6, '0')}`;
  } while (usedColors.has(color));
  return color;
};

export const createUniqueColors = (
  items: { color: string | undefined; id: string }[],
  usedColors: Set<string>,
  conditionColorMap: Map<string, string>,
) => {
  items.forEach((item) => {
    if (item.color && !usedColors.has(item.color)) {
      usedColors.add(item.color);
      conditionColorMap.set(item.id, item.color);
    } else if (!conditionColorMap.has(item.id)) {
      const newColor = getUniqueColor(usedColors);
      item.color = newColor;
      usedColors.add(newColor);
      conditionColorMap.set(item.id, newColor);
    } else {
      item.color = conditionColorMap.get(item.id);
    }
  });
};

/**
 * Finds the extreme value (minimum or maximum) in an array of values after applying a transformation function.
 *
 * Use in place of `Math.min(...values)` or `Math.max(...values)` for large arrays.
 *
 * @param values - The array of values to search through.
 * @param extreme - Specifies whether to find the minimum or maximum value.
 * @param {(value: T) => number} [valueTransform=(value) => value as number] - A function to transform each value in
 *   the array to a number for comparison.
 */
export function optimizedExtreme<T>(
  values: T[],
  extreme: 'min' | 'max',
  valueTransform: (value: T) => number = (value) => value as unknown as number,
): number {
  let extremeValue = extreme === 'max' ? -Infinity : Infinity;

  for (const value of values) {
    const dateValue = valueTransform(value);
    if (extreme === 'max') {
      if (dateValue > extremeValue) extremeValue = dateValue;
    } else {
      if (dateValue < extremeValue) extremeValue = dateValue;
    }
  }

  return extremeValue;
}

export function isMultipleValues(value: string): boolean {
  return value === t('VANTAGE.EVIDENCE_TABLE.MULTIPLE_VALUES');
}

interface TranslatableLabel {
  label?: string;
  labelTranslationKey?: string;
}

export function translateLabelIfPossible(item: TranslatableLabel | undefined): string {
  return (item?.labelTranslationKey ? t(item.labelTranslationKey) : item?.label) ?? '*missing label*';
}

// TODO : CRAB-46379 : Remove the hardcoded pinning of reason codes once pinning works in the UI
export function collapseNonPinnedExtensionUi(): void {
  setActiveExtensions(
    sqVantageWorkbookStore.activeExtensions.filter(
      (ext) => sqVantageContextStore.pinnedExtensions.includes(ext) || ext === VantageNames.Extensions.ReasonCodes,
    ),
  );
}

export const getExtensions = (id: string): Promise<ExtensionSchema[]> => safeGetJsonProperty(id, 'extensions', []);

/**
 * Searches an array and replaces the first item matching the given predicate;
 * if none is found, appends the item to the array.
 *
 * @param items - array of items
 * @param item - item to replace existing item with or append to array
 * @param finder - predicate to find the item to replace
 */
export const replaceOrAppend = <T>(items: T[], item: T, finder: (arg: T) => boolean): T[] => {
  const index = items.findIndex(finder);
  const itemsCopy = [...items];
  if (index !== -1) {
    itemsCopy[index] = item;
  } else {
    itemsCopy.push(item);
  }
  return itemsCopy;
};

export const isVantageSystemContextName = (name: string): boolean =>
  name.startsWith(SeeqNames.SeeqContextNameSpace.VantageLabelCategoryNamespace);

/**
 * Get the context display name
 *
 * @param contextName - The full context name (e.g., "__Vantage.flag")
 * @returns The context display name, or the original context name if it's not a Vantage system context.
 */
export const getVantageSystemContextName = (contextName: string): string => {
  if (!isVantageSystemContextName(contextName)) {
    return contextName;
  }
  const parts = contextName.split('.');
  if (parts.length < 2) {
    return contextName;
  }
  return parts[1];
};

export const isUserDefinedContextName = (name: string): boolean =>
  name.startsWith(VantageNames.UserDefinedContext.ContextNamePrefix);

/**
 * Splits a user-defined context name into its type and display name components.
 *
 * @param contextName - The full context name (e.g., "userDefinedContext.Label.My Context")
 * @returns An object containing the context type and display name, or null if the name is invalid.
 */
export const splitUserDefinedContextName = (
  contextName: string,
): { type: UserDefinedContextType; name: string } | null => {
  if (!isUserDefinedContextName(contextName)) {
    return null;
  }
  const parts = contextName.split('.');
  if (parts.length < 3) {
    return null;
  }
  // Get context type from extension name
  const typeString = parts[1];
  const type = isUserDefinedContextType(typeString) ? typeString : null;
  if (!type) {
    return null;
  }
  const name = parts.slice(2).join('.');
  return { name, type };
};

export const startsWithContextNamePrefix = (name: string): boolean =>
  isVantageSystemContextName(name) || isUserDefinedContextName(name);

/**
 * Gets the display name of a context, handling both user-defined and Vantage system contexts.
 * @param contextName - The full context name (e.g., "userDefinedContext.Label.My Context" or "__Vantage.flag")
 * @returns The display name of the context
 */
export const getContextDisplayName = (contextName: string): string =>
  capitalizeString(splitUserDefinedContextName(contextName)?.name ?? getVantageSystemContextName(contextName));

export const isValidContextKind = (kind: string | undefined): boolean =>
  kind === VantageNames.Kinds.Asset || kind === VantageNames.Kinds.Capsule || kind === VantageNames.Kinds.Condition;

export const isContextDisabledForSelection = (
  kind: string | undefined,
  useAssetNameForContextLookup: boolean | undefined = false,
): boolean => !isValidContextKind(kind) || (useAssetNameForContextLookup === true && kind === VantageNames.Kinds.Asset);
